<template>
  <q-img
    v-if="useOfficialSymbol && !useMPAFilmRatingsSymbol"
    :src="symbolUrl"
    :style="{
      height: `${symbolSize}${unitOfMeasure}`,
      'width': squareSymbol ? `${symbolSize}${unitOfMeasure}` : undefined,
      'max-width': squareSymbol ? `${symbolSize}${unitOfMeasure}` : `${symbolSize}${unitOfMeasure}`
    }"
    fit="contain"
    :class="{ 'q-pa-xs': addPadding }"
    :alt="`${$t('movie.movieRatedValue', { value: rated })}. ${ratingReason ? ratingReason : ''}`"
  >
    <q-tooltip
      v-if="ratingReason"
    >
      {{ ratingReason }}
    </q-tooltip>
  </q-img>
  <q-chip
    v-else
    square
    :class="{ 'bg-red': hightlight }"
    :outline="outline"
    :color="color"
    :text-color="configTextColor"
    :dense="dense"
    :size="size"
    :style="{
      'font-size': !!fontSize ? `${fontSize}${unitOfMeasure}` : undefined,
      'border-radius': `${borderRadius}px`
    }"
  >
    <img
      v-if="useOfficialSymbol && useMPAFilmRatingsSymbol"
      :src="symbolUrl"
      :style="{
        filter: configTextColor === 'white' || lightText ? 'invert(100%) sepia(0%) saturate(7500%) hue-rotate(330deg) brightness(102%) contrast(101%)' : undefined,
        height: size === '2vw' ? '2vw' : `${symbolSize}${unitOfMeasure}`,
      }"
      :alt="`${$t('movie.movieRatedValue', { value: rated })}. ${ratingReason ? ratingReason : ''}`"
    >
    <template v-else>
      {{ rated }}
    </template>
    <q-tooltip
      v-if="ratingReason"
    >
      {{ ratingReason }}
    </q-tooltip>
  </q-chip>
</template>

<script>
import { isNARegion, region } from 'src/config'
export default {
  name: 'Rated',
  props: {
    rated: {
      type: String,
      required: true
    },
    ratingReason: {
      type: String,
      required: false,
      default: null
    },
    size: {
      type: String,
      required: false,
      default: null
    },
    outline: {
      type: Boolean,
      required: false,
      default: true
    },
    dense: {
      type: Boolean,
      required: false,
      default: true
    },
    color: {
      type: String,
      required: false,
      default: 'responsive'
    },
    textColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    borderRadius: {
      type: Number,
      required: false,
      default: 3
    },
    fontSize: {
      type: Number,
      required: false,
      default: null
    },
    lightText: {
      type: Boolean,
      required: false,
      default: false
    },
    addPadding: {
      type: Boolean,
      required: false,
      default: false
    },
    isFontSizeResponsive: {
      type: Boolean,
      required: false,
      default: false
    },
    disableOfficialSymbol: {
      type: Boolean,
      required: false,
      default: false
    },
    highlightRRated: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  configuration: ['currentSite'],
  computed: {
    ratedInUpperCase () {
      return this.rated.replace(/\s+/g, '').toUpperCase()
    },
    useOfficialSymbol () {
      if (this.disableOfficialSymbol) return false

      if (isNARegion) {
        const usRatings = ['G', 'NC-17', 'PG', 'PG-13', 'R']
        return _.includes(usRatings, this.ratedInUpperCase)
      }
      if (region === 'uk') {
        const ukRatings = ['PG', '12', '12A', '15', '18', 'R18', 'U']
        return _.includes(ukRatings, this.ratedInUpperCase)
      }
      if (region === 'au') {
        const auRatings = ['PG', 'CTC', 'G', 'M', 'MA15+', 'R18+', 'X18+', 'E']
        return _.includes(auRatings, this.ratedInUpperCase)
      }
      if (region === 'nz') {
        const nzRatings = ['PG', 'G', '13', '15', '16', '18', 'G', 'M', 'R', 'RESTRICTED', 'RP13', 'RP15', 'RP16', 'RP18', 'R13', 'R15', 'R16', 'R18']
        return _.includes(nzRatings, this.ratedInUpperCase)
      }
      return false
    },
    symbolUrl () {
      if (isNARegion) {
        return `/images/film-ratings/us/${this.ratedInUpperCase}.svg`
      }

      return `/images/film-ratings/${region}/${this.ratedInUpperCase}${region === 'au' ? '.png' : '.svg'}`
    },
    useMPAFilmRatingsSymbol () {
      return isNARegion
    },
    hightlight () {
      return this.highlightRRated && this.ratedInUpperCase === 'R'
    },
    symbolSize () {
      if (isNARegion) {
        if (this.size && this.size === 'xl') return 16
        if (this.size && this.size === 'lg') return 14
        if (this.size && this.size === 'md') return 12
        if (this.size && this.size === 'xs') return 10
        if (this.fontSize) return this.fontSize
        return 9
      } else {
        if (this.size && this.size === '2vw') return 60
        if (this.size && this.size === 'xl') return 40
        if (this.size && this.size === 'lg') return 27
        if (this.size && this.size === 'md') return 24
        if (this.size && this.size === 'sm') return 16
        if (this.fontSize) return this.fontSize / 0.6
        return 20
      }
    },
    squareSymbol () {
      if (region === 'uk' && (this.ratedInUpperCase === 'PG' || this.ratedInUpperCase === 'U')) return false
      return true
    },
    configTextColor () {
      const themeColor = ['primary', 'secondary', 'header', 'accent', 'positive', 'negative', 'info', 'warning', 'dark']
      if (this.outline) return this.textColor

      if (this.color && _.includes(themeColor, this.color)) {
        const backgroundColorHex = this.$getSiteColorHex(this.currentSite, this.color)
        if (!backgroundColorHex) return 'white'
        return this.$isColorDark(backgroundColorHex) ? 'white' : 'black'
      }

      return this.textColor
    },
    unitOfMeasure () {
      return this.isFontSizeResponsive ? 'vw' : 'px'
    }
  },
  created () {
    _.extend(this, { isNARegion })
  }
}
</script>

<style lang="scss">
  .mp-rating {
    display: inline-block;
    border: solid 1px;
    font-weight: bold;
    padding: 0 4px;
    border-radius: 3px;
    margin: 0;
    line-height: 18px;
  }
  .mp-rating.size-sm {
    line-height: 12px;
    font-size: 12px;
  }
  .contain .q-img__image {
    background-size: contain !important;
  }
</style>
